import React, { useState, useMemo } from 'react';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { LightTooltip } from 'apps/sigma2-ui/src/components/ui/Tooltip';
import { useCallback } from 'react';
import { useOnClickOutside } from 'apps/sigma2-ui/src/explore-section/hooks/useOnClickOutside';
import { ColorCirclePicker } from 'apps/sigma2-ui/src/explore-section/components/ColorCirclePicker';
import { TouchTarget } from 'apps/sigma2-ui/src/components/ui/TouchTarget';

export interface TableTooltipProps {
  baseColors: string[];
  onColorChange: (belowColor: string, aboveColor: string) => void;
}

export const TableColorTooltip = ({
  baseColors,
  onColorChange,
}: TableTooltipProps) => {
  const [belowColor, aboveColor] = useMemo(() => {
    return [baseColors[0] ?? '#F7F7F7', baseColors[1] ?? '#F7F7F7'];
  }, [baseColors]);

  const [isBelowColorPickerOpen, setIsBelowColorPickerOpen] =
    useState<boolean>(false);
  const [isAboveColorPickerOpen, setIsAboveColorPickerOpen] =
    useState<boolean>(false);

  const handleBelowColorPickerOpen = useCallback(() => {
    setIsBelowColorPickerOpen(true);
  }, []);

  const handleBelowColorPickerClose = useCallback(() => {
    setIsBelowColorPickerOpen(false);
  }, []);

  const handleAboveColorPickerOpen = useCallback(() => {
    setIsAboveColorPickerOpen(true);
  }, []);

  const handleAboveColorPickerClose = useCallback(() => {
    setIsAboveColorPickerOpen(false);
  }, []);

  const handleColorChange = useCallback(
    (bC: string, aC: string) => {
      onColorChange(bC, aC);
      handleBelowColorPickerClose();
      handleAboveColorPickerClose();
    },
    [onColorChange, handleBelowColorPickerClose, handleAboveColorPickerClose],
  );

  const belowColorPickerRef = useOnClickOutside({
    handleClickOutside: handleBelowColorPickerClose,
  });

  const aboveColorPickerRef = useOnClickOutside({
    handleClickOutside: handleAboveColorPickerClose,
  });

  const gradientStyle = useMemo(
    () => ({
      background: `linear-gradient(90deg, ${belowColor} 0%, #F7F7F7 47.02%, #F7F7F7 55.73%, ${aboveColor} 100%)`,
    }),
    [belowColor, aboveColor],
  );

  const handleReset = useCallback(() => {
    handleColorChange('#F7F7F7', '#F7F7F7');
  }, [handleColorChange]);

  const language = useLanguage();

  return (
    <div className="flex flex-col p-6">
      <div className="flex justify-between mb-1.5">
        <S2Typography
          variant="body-6-medium"
          translatedContent={language.addColorScale()}
          textColorVariant="gray-900"
        />

        <TouchTarget
          className="rounded bg-gray-100 px-2.5"
          behavior={{ type: 'onClick', onClick: handleReset }}
          content={() => {
            return (
              <div>
                <S2Typography
                  variant="body-6"
                  translatedContent={language.reset()}
                  textColorVariant="gray-900"
                />
              </div>
            );
          }}
        />
      </div>
      <div className="flex w-full">
        <S2Typography
          variant="body-7"
          translatedContent={language.setColorsForOrgAvg()}
          textColorVariant="gray-900"
        />
      </div>
      <div className="flex flex-col pt-6">
        <div className="flex w-full justify-between">
          <S2Typography
            variant="body-7"
            translatedContent="Below"
            textColorVariant="gray-500"
          />
          <S2Typography
            variant="body-7"
            translatedContent=""
            textColorVariant="gray-500"
          />
          <S2Typography
            variant="body-7"
            translatedContent="Above"
            textColorVariant="gray-500"
          />
        </div>
        <div
          className="flex w-full h-9 rounded pt-1.5"
          style={gradientStyle}
        ></div>
        <div className="flex w-full justify-between pt-1.5">
          <LightTooltip
            open={isBelowColorPickerOpen}
            arrow={true}
            placement="bottom"
            title={
              <div ref={belowColorPickerRef}>
                <ColorCirclePicker
                  baseColor={belowColor}
                  onColorChange={(color) => {
                    handleColorChange(color, aboveColor);
                  }}
                />
              </div>
            }
            className="[&>div]:!p-0"
            disableHoverListener={true}
            disableTouchListener={true}
          >
            <div
              onClick={handleBelowColorPickerOpen}
              className="w-6 h-6 rounded-full"
              style={{ background: belowColor }}
            ></div>
          </LightTooltip>

          <LightTooltip
            open={isAboveColorPickerOpen}
            arrow={true}
            placement="bottom"
            title={
              <div ref={aboveColorPickerRef}>
                <ColorCirclePicker
                  baseColor={aboveColor}
                  onColorChange={(color) => {
                    handleColorChange(belowColor, color);
                  }}
                />
              </div>
            }
            className="[&>div]:!p-0"
            disableHoverListener={true}
            disableTouchListener={true}
          >
            <div
              onClick={handleAboveColorPickerOpen}
              className="w-6 h-6 rounded-full"
              style={{ background: aboveColor }}
            ></div>
          </LightTooltip>
        </div>
      </div>
    </div>
  );
};
